import { useEffect, useState } from 'react';
import { authedFetch, type SessionState } from '../lib/session.js';
import { diaHora } from '../lib/fechas.js';

/**
 * A MEETING RECORDED WITH FLUXO PARA MEET, open on the right of the meetings page (#/reunion/<id>,
 * also the link in the GHL note). Laid out like a Granola note (27-oct, Joan: «en un entorno como
 * el de Granola»; read from Granola 7.668.3 with REA): the title in a serif, the facts as chips
 * under it, and two tabs: the summary, open by default (Granola's «Enhanced»), and the transcript
 * as a conversation, the recorder's mic on the right and the others on the left with the name Meet
 * showed («Otros» when it could not be known; a name taken from the conversation says so). Without
 * a contact, its email can be written here (the same lookup in the Fluxo CRM as the extension's):
 * the note then goes out in the next round of the worker.
 */

type Origen = 'micro' | 'meet' | 'contexto' | null;
interface Linea { t: number; canal: 'micro' | 'pestana' | null; quien: string | null; por: Origen; texto: string }
export interface VistaReunion {
  id: string;
  codigo: string;
  inicio: string;
  fin: string | null;
  estado: 'grabando' | 'subida' | 'lista' | 'enviada' | 'fallida' | 'cerrada';
  resultado: 'reunion' | 'no_show' | 'prueba' | null;
  canales: 1 | 2;
  contacto: { nombre: string; email: string | null } | null;
  notaEnGhl: boolean;
  resumen: string | null;
  siguienteAccion: string | null;
  objecion: string | null;
  dialogo: Linea[];
  /** The names Meet showed in the call. */
  participantes?: string[];
}

export const reloj = (s: number) => {
  const t = Math.max(0, Math.floor(s));
  const h = Math.floor(t / 3600);
  const mm = String(Math.floor((t % 3600) / 60)).padStart(2, '0');
  const ss = String(t % 60).padStart(2, '0');
  return h ? `${h}:${mm}:${ss}` : `${mm}:${ss}`;
};

/** Who a line is from as the page shows it: a one-channel recording (0.4) has no names. */
export function etiquetaLinea(l: Pick<Linea, 'canal' | 'quien'>): string | null {
  return l.quien ?? (l.canal === 'pestana' ? 'Otros' : null);
}

/** What is going on with the meeting, in one sentence, when there is nothing else to show yet. */
export function estadoDe(r: Pick<VistaReunion, 'estado' | 'resultado'>): string | null {
  if (r.estado === 'grabando' || r.estado === 'subida') return 'Se está transcribiendo. La transcripción sale aquí al terminar.';
  if (r.estado === 'fallida') return 'No se pudo transcribir esta reunión.';
  if (r.resultado === 'no_show') return 'No show: solo habló quien grababa. No va a GHL.';
  if (r.resultado === 'prueba') return 'Prueba: no era una reunión. No va a GHL.';
  return null;
}

/** «32 min», «1 h 05 min»; without an end (still recording, or an old one), nothing. */
export function duracion(inicio: string, fin: string | null): string | null {
  if (!fin) return null;
  const min = Math.round((Date.parse(fin) - Date.parse(inicio)) / 60_000);
  if (!Number.isFinite(min) || min < 1) return null;
  return min < 60 ? `${min} min` : `${Math.floor(min / 60)} h ${String(min % 60).padStart(2, '0')} min`;
}

/** The summary's lines: «- Dolor: x» becomes a labelled point; anything else, a plain one. */
export function puntosDelResumen(resumen: string): Array<{ etiqueta: string | null; texto: string }> {
  return resumen.split('\n').map((l) => l.trim().replace(/^[-•*]\s*/, '')).filter(Boolean).map((l) => {
    const m = /^([^:]{1,30}):\s+(.+)$/.exec(l);
    return m ? { etiqueta: m[1], texto: m[2] } : { etiqueta: null, texto: l };
  });
}

export interface Turno { quien: string | null; mio: boolean; t: number; contexto: boolean; textos: string[] }
/** The transcript in turns: consecutive lines of the same person (and channel) go in one bubble. */
export function turnos(dialogo: Linea[]): Turno[] {
  const out: Turno[] = [];
  for (const l of dialogo) {
    const quien = etiquetaLinea(l);
    const mio = l.canal === 'micro';
    const ultimo = out[out.length - 1];
    if (ultimo && ultimo.quien === quien && ultimo.mio === mio && ultimo.contexto === (l.por === 'contexto')) ultimo.textos.push(l.texto);
    else out.push({ quien, mio, t: l.t, contexto: l.por === 'contexto', textos: [l.texto] });
  }
  return out;
}

const puedePonerContacto = (r: VistaReunion) => !r.contacto && ['grabando', 'subida', 'lista'].includes(r.estado);

/** The title of a meeting: its contact or, without one, the names Meet showed. */
export function tituloReunion(r: { contacto: VistaReunion['contacto']; participantes?: string[] }): string {
  if (r.contacto) return r.contacto.nombre || r.contacto.email || 'Contacto sin nombre';
  return r.participantes?.length ? r.participantes.join(', ') : 'Reunión sin contacto';
}

type Pestana = 'resumen' | 'transcripcion';

/** The summary as text to paste in an email or a WhatsApp: who and when, the points, the next step. */
export function textoResumen(r: VistaReunion, zona = 'Europe/Madrid'): string {
  const lineas = [`${tituloReunion(r)} · ${diaHora(r.inicio, zona)}`, ''];
  if (r.resumen) lineas.push(...puntosDelResumen(r.resumen).map((p) => `- ${p.etiqueta ? `${p.etiqueta}: ` : ''}${p.texto}`));
  if (r.siguienteAccion) lineas.push('', `Siguiente acción: ${r.siguienteAccion}`);
  if (r.objecion) lineas.push(`Objeción: ${r.objecion}`);
  return lineas.join('\n').trim();
}
/** The transcript as text: «[01:05] Sonia Netto: …», a line per turn. */
export function textoTranscripcion(r: Pick<VistaReunion, 'dialogo'>): string {
  return turnos(r.dialogo).map((t) => `[${reloj(t.t)}] ${t.quien ? `${t.quien}: ` : ''}${t.textos.join(' ')}`).join('\n');
}
/** To the clipboard; inside GHL's iframe the clipboard API can be refused, then the old way. */
async function alPortapapeles(texto: string): Promise<boolean> {
  try { await navigator.clipboard.writeText(texto); return true; } catch { /* the old way below */ }
  try {
    const area = document.createElement('textarea');
    area.value = texto; area.setAttribute('readonly', ''); area.style.position = 'fixed'; area.style.opacity = '0';
    document.body.appendChild(area); area.select();
    const hecho = document.execCommand('copy');
    area.remove();
    return hecho;
  } catch { return false; }
}

/**
 * `previa`: what the list already knows of it (title, participants), shown while it loads.
 * `inicial`: what to show without fetching (tests and previews); the page fetches it otherwise.
 */
export function ReunionAbierta({ session, id, zona = 'Europe/Madrid', previa = null, inicial = null }: {
  session: SessionState; id: string; zona?: string;
  previa?: Pick<VistaReunion, 'contacto' | 'participantes' | 'inicio' | 'fin' | 'codigo' | 'notaEnGhl'> | null;
  inicial?: VistaReunion | null;
}) {
  const [r, setR] = useState<VistaReunion | null>(inicial);
  const [err, setErr] = useState<string | null>(null);
  const [pestana, setPestana] = useState<Pestana | null>(null);
  const [email, setEmail] = useState('');
  const [guardando, setGuardando] = useState(false);
  const [errEmail, setErrEmail] = useState<string | null>(null);
  const [copiado, setCopiado] = useState<string | null>(null);

  useEffect(() => {
    if (inicial) return undefined;
    let cancelada = false;
    void (async () => {
      try {
        const res = await authedFetch(session, `/api/reuniones-meet/${encodeURIComponent(id)}`);
        if (res.status === 404) throw new Error('Esta reunión no existe o ya se borró (se borran a los 90 días).');
        if (!res.ok) throw new Error(`No se pudo cargar la reunión (${res.status}).`);
        const datos = (await res.json()) as VistaReunion;
        if (!cancelada) setR(datos);
      } catch (e) {
        if (!cancelada) setErr(e instanceof Error ? e.message : String(e));
      }
    })();
    return () => { cancelada = true; };
  }, [id, session, inicial]);

  async function ponerContacto(e: React.FormEvent) {
    e.preventDefault();
    setGuardando(true);
    setErrEmail(null);
    try {
      const res = await authedFetch(session, `/api/reuniones-meet/${encodeURIComponent(id)}/contacto`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ email }),
      });
      const datos = await res.json().catch(() => ({})) as Partial<VistaReunion> & { error?: string; detail?: string };
      if (!res.ok) throw new Error(datos.detail ?? (datos.error === 'email_invalido' ? 'Ese email no es válido.' : 'No se pudo guardar el contacto.'));
      setR(datos as VistaReunion);
    } catch (x) {
      setErrEmail(x instanceof Error ? x.message : String(x));
    } finally {
      setGuardando(false);
    }
  }

  const cabeza = r ?? previa;
  const dura = cabeza ? duracion(cabeza.inicio, cabeza.fin) : null;
  const estado = r ? estadoDe(r) : null;
  // The summary first, as in Granola; with no summary yet but a transcript, the transcript.
  const vista: Pestana = pestana ?? (r && !r.resumen && r.dialogo.length ? 'transcripcion' : 'resumen');
  const hayQueCopiar = r ? (vista === 'resumen' ? !!(r.resumen || r.siguienteAccion || r.objecion) : r.dialogo.length > 0) : false;
  async function copiar() {
    if (!r) return;
    const hecho = await alPortapapeles(vista === 'resumen' ? textoResumen(r, zona) : textoTranscripcion(r));
    setCopiado(hecho ? 'Copiado' : 'No se ha podido copiar');
    setTimeout(() => setCopiado(null), 2000);
  }

  return (
    <article className="sala-reunion" aria-label="Reunión grabada">
      <a className="sala-atras" href="#/reuniones-grabadas">← Reuniones</a>
      <h1 className="sala-reunion-titulo">{cabeza ? tituloReunion(cabeza) : 'Reunión grabada'}</h1>
      {cabeza && (
        <div className="sala-chips">
          <span className="sala-chip">{diaHora(cabeza.inicio, zona)}</span>
          {dura && <span className="sala-chip">{dura}</span>}
          <span className="sala-chip">Google Meet {cabeza.codigo}</span>
          {cabeza.contacto && !!cabeza.participantes?.length && <span className="sala-chip">{cabeza.participantes.join(', ')}</span>}
          {cabeza.notaEnGhl && <span className="sala-chip sala-chip--acento">Nota en GHL</span>}
        </div>
      )}

      {err && <p className="sala-aviso" role="alert">{err}</p>}
      {!r && !err && <p className="sala-vacio">Cargando…</p>}

      {r && (
        <>
          {estado && <p className="sala-aviso">{estado}</p>}

          {puedePonerContacto(r) && (
            <form className="sala-contacto" onSubmit={ponerContacto}>
              <label htmlFor="reunion-email">No se ha podido saber el contacto. Su email:</label>
              <input id="reunion-email" type="email" required value={email} onChange={(e) => setEmail(e.target.value)} placeholder="nombre@empresa.com" />
              <button type="submit" disabled={guardando}>{guardando ? 'Guardando…' : 'Guardar en GHL'}</button>
              {errEmail && <span role="alert" className="sala-contacto-error">{errEmail}</span>}
            </form>
          )}

          <div className="sala-barra">
            <div className="sala-pestanas" role="tablist" aria-label="Qué ver">
              <button type="button" role="tab" aria-selected={vista === 'resumen'} className={vista === 'resumen' ? 'activa' : ''} onClick={() => setPestana('resumen')}>Resumen</button>
              <button type="button" role="tab" aria-selected={vista === 'transcripcion'} className={vista === 'transcripcion' ? 'activa' : ''} onClick={() => setPestana('transcripcion')}>Transcripción</button>
            </div>
            {hayQueCopiar && (
              <button type="button" className="sala-copiar" onClick={() => void copiar()} aria-live="polite">
                {copiado ?? (vista === 'resumen' ? 'Copiar resumen' : 'Copiar transcripción')}
              </button>
            )}
          </div>

          {vista === 'resumen' ? <Resumen r={r} /> : <Transcripcion r={r} />}
        </>
      )}
    </article>
  );
}

/** The summary as a short document: one point per line, its label in bold, then the next step. */
export function Resumen({ r }: { r: Pick<VistaReunion, 'resumen' | 'siguienteAccion' | 'objecion'> }) {
  if (!r.resumen && !r.siguienteAccion && !r.objecion) return <p className="sala-vacio">Todavía no hay resumen.</p>;
  return (
    <section className="sala-doc">
      {r.resumen && (
        <ul>
          {puntosDelResumen(r.resumen).map((p, i) => (
            <li key={i}>{p.etiqueta && <strong>{p.etiqueta}:</strong>} {p.texto}</li>
          ))}
        </ul>
      )}
      {r.siguienteAccion && <p className="sala-doc-extra"><strong>Siguiente acción:</strong> {r.siguienteAccion}</p>}
      {r.objecion && <p className="sala-doc-extra"><strong>Objeción:</strong> {r.objecion}</p>}
    </section>
  );
}

/** The transcript as a conversation: the recorder's mic on the right, everyone else on the left. */
export function Transcripcion({ r }: { r: Pick<VistaReunion, 'dialogo' | 'canales'> }) {
  if (!r.dialogo.length) return <p className="sala-vacio">Todavía no hay transcripción.</p>;
  return (
    <section className="sala-chat">
      {r.canales === 1 && <p className="sala-nota">Grabada con una versión anterior de la extensión: sin separar quién habla.</p>}
      {turnos(r.dialogo).map((t, i) => (
        <div key={i} className={`sala-turno${t.mio ? ' sala-turno--mio' : ''}`}>
          <div className="sala-turno-quien">
            {t.quien && <strong>{t.quien}</strong>}
            {t.contexto && <span title="Meet no lo marcó; se deduce de la conversación"> (por contexto)</span>}
            <span className="sala-turno-hora">{reloj(t.t)}</span>
          </div>
          <div className="sala-burbuja">{t.textos.map((x, j) => <p key={j}>{x}</p>)}</div>
        </div>
      ))}
    </section>
  );
}
